import { useState, useEffect } from 'react';
import { FaGift, FaUsers, FaStar, FaSearch, FaTrash, FaEye, FaClock, FaCheck, FaShoppingCart, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import EmptyState from '../../components/ui/EmptyState';

const ReferralManagement = () => {
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchReferrals();
  }, [pagination.page, statusFilter]);

  const fetchReferrals = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/referrals/admin/all?status=${statusFilter}&page=${pagination.page}&limit=${pagination.limit}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setReferrals(data.referrals);
        setStats(data.stats);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      } else {
        toast.error('Failed to load referrals');
      }
    } catch (error) {
      console.error('Error fetching referrals:', error);
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReferral = async (referralId) => {
    if (!window.confirm('Are you sure you want to delete this referral?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/referrals/admin/${referralId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Referral deleted successfully');
        fetchReferrals();
      } else {
        toast.error('Failed to delete referral');
      }
    } catch (error) {
      console.error('Error deleting referral:', error);
      toast.error('Failed to delete referral');
    }
  };

  const handleUpdateStatus = async (referralId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/referrals/admin/${referralId}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ status: newStatus })
        }
      );

      if (response.ok) {
        toast.success('Status updated successfully');
        fetchReferrals();
        setShowDetailModal(false);
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FaClock className="status-icon pending" />;
      case 'registered':
        return <FaCheck className="status-icon registered" />;
      case 'purchased':
        return <FaShoppingCart className="status-icon purchased" />;
      case 'rewarded':
        return <FaStar className="status-icon rewarded" />;
      default:
        return <FaClock className="status-icon" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'registered':
        return 'Registered';
      case 'purchased':
        return 'Purchased';
      case 'rewarded':
        return 'Rewarded';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredReferrals = referrals.filter(referral => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      referral.referredEmail?.toLowerCase().includes(search) ||
      referral.referredName?.toLowerCase().includes(search) ||
      referral.referrer?.name?.toLowerCase().includes(search) ||
      referral.referrer?.email?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl shadow-lg">
          <FaGift className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Referral Management</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage and track all user referrals</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-5 shadow-card">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <FaUsers className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalReferrals || 0}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Referrals</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-5 shadow-card">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-orange-100 dark:bg-orange-900/30 rounded-xl">
              <FaClock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.pendingReferrals || 0}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Pending</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-5 shadow-card">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
              <FaCheck className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.registeredReferrals || 0}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Registered</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-5 shadow-card">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-teal-100 dark:bg-teal-900/30 rounded-xl">
              <FaShoppingCart className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.purchasedReferrals || 0}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Purchased</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-5 shadow-card">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/20 rounded-xl">
              <FaStar className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.totalPointsGiven || 0}</p>
              <p className="text-xs text-white/80">Points Given</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="px-4 py-2.5 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="registered">Registered</option>
            <option value="purchased">Purchased</option>
            <option value="rewarded">Rewarded</option>
          </select>
        </div>
      </div>

      {/* Referrals Table */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card overflow-hidden">
        {loading ? (
          <SkeletonLoader variant="table" rows={6} cols={8} />
        ) : filteredReferrals.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                    <th className="px-6 py-4 text-left text-sm font-semibold">Referrer</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Referred Email</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Referred Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Service</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Points</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                  {filteredReferrals.map((referral) => (
                    <tr key={referral.id} className="hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{referral.referrer?.name || '-'}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{referral.referrer?.email || '-'}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{referral.referredEmail}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{referral.referredName || referral.referredUser?.name || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${
                          referral.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                          referral.status === 'registered' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                          referral.status === 'purchased' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        }`}>
                          {getStatusIcon(referral.status)}
                          {getStatusLabel(referral.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{referral.purchasedService || '-'}</td>
                      <td className="px-6 py-4">
                        {referral.status === 'rewarded' || referral.status === 'purchased' ? (
                          <span className="font-semibold text-green-600 dark:text-green-400">{referral.rewardAmount} pts</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{formatDate(referral.createdAt)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setSelectedReferral(referral); setShowDetailModal(true); }} className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="View Details">
                            <FaEye className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteReferral(referral.id)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Delete">
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-center gap-4 p-4 border-t border-gray-100 dark:border-dark-700">
              <button
                disabled={pagination.page <= 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                className={`p-2 rounded-lg transition-all ${pagination.page <= 1 ? 'text-gray-300 dark:text-dark-600 cursor-not-allowed' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700'}`}
              >
                <FaChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Page <span className="font-medium">{pagination.page}</span> of <span className="font-medium">{pagination.pages}</span>
              </span>
              <button
                disabled={pagination.page >= pagination.pages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                className={`p-2 rounded-lg transition-all ${pagination.page >= pagination.pages ? 'text-gray-300 dark:text-dark-600 cursor-not-allowed' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700'}`}
              >
                <FaChevronRight className="w-4 h-4" />
              </button>
            </div>
          </>
        ) : (
          <EmptyState icon={FaUsers} preset="search" title="No Referrals Found" description="Referrals will appear here once users start referring others." />
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedReferral && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowDetailModal(false)}>
          <div className="w-full max-w-2xl bg-white dark:bg-dark-800 rounded-2xl shadow-3d max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-dark-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Referral Details</h2>
              <button onClick={() => setShowDetailModal(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg transition-colors">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Referrer Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Name</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedReferral.referrer?.name || '-'}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Email</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedReferral.referrer?.email || '-'}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Referred Person</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Email</p>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{selectedReferral.referredEmail}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Name</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedReferral.referredName || selectedReferral.referredUser?.name || '-'}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Registered</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedReferral.referredUser ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Referral Status</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current Status</p>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${
                      selectedReferral.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                      selectedReferral.status === 'registered' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                      selectedReferral.status === 'purchased' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    }`}>
                      {getStatusLabel(selectedReferral.status)}
                    </span>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reward Points</p>
                    <p className="font-semibold text-green-600 dark:text-green-400">{selectedReferral.rewardAmount} pts</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reward Applied</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedReferral.rewardApplied ? 'Yes' : 'No'}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Service Purchased</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedReferral.purchasedService || '-'}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Timeline</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Invitation Sent</p>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{formatDate(selectedReferral.invitationSentAt)}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Registered At</p>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{formatDate(selectedReferral.registeredAt)}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Purchased At</p>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{formatDate(selectedReferral.purchasedAt)}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Update Status</h3>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => handleUpdateStatus(selectedReferral.id, 'pending')} className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${selectedReferral.status === 'pending' ? 'bg-yellow-500 text-white' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'}`}>
                    Pending
                  </button>
                  <button onClick={() => handleUpdateStatus(selectedReferral.id, 'registered')} className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${selectedReferral.status === 'registered' ? 'bg-purple-500 text-white' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/50'}`}>
                    Registered
                  </button>
                  <button onClick={() => handleUpdateStatus(selectedReferral.id, 'purchased')} className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${selectedReferral.status === 'purchased' ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50'}`}>
                    Purchased
                  </button>
                  <button onClick={() => handleUpdateStatus(selectedReferral.id, 'rewarded')} className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${selectedReferral.status === 'rewarded' ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'}`}>
                    Rewarded
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralManagement;
